import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
    Loader2, Shield, ShieldCheck, Smartphone, Mail, Copy, Download,
    ArrowLeft, KeyRound, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { twoFAApi } from '../../lib/api';

/**
 * Pflicht-2FA-Setup für neu angelegte Admins (vor JWT-Ausgabe).
 *
 * Flow:
 *   1. method      — User wählt TOTP oder E-Mail
 *   2. totp/email  — Setup-Daten anzeigen (QR / Mail) + Code-Eingabe
 *   3. backup      — Backup-Codes anzeigen, Download + Copy, "Fertig"-Button
 */
export function ForcedTwoFASetup({ session, onCompleted, onCancel }) {
    const [step, setStep] = useState('method');           // method | totp | email | backup
    const [method, setMethod] = useState(null);           // 'totp' | 'email'
    const [loading, setLoading] = useState(false);
    const [setupData, setSetupData] = useState(null);     // { qr_code, secret, account } | { email }
    const [code, setCode] = useState('');
    const [backupCodes, setBackupCodes] = useState([]);
    const [finalLogin, setFinalLogin] = useState(null);   // { token, user }
    const [downloaded, setDownloaded] = useState(false);
    const [copied, setCopied] = useState(false);

    const startTotp = async () => {
        setLoading(true);
        try {
            const res = await twoFAApi.forcedSetupBeginTotp(session.setup_id);
            setSetupData(res.data);
            setMethod('totp');
            setStep('totp');
        } catch (e) {
            toast.error(e.response?.data?.detail || '2FA-Setup konnte nicht gestartet werden');
        } finally {
            setLoading(false);
        }
    };

    const startEmail = async () => {
        setLoading(true);
        try {
            const res = await twoFAApi.forcedSetupBeginEmail(session.setup_id);
            setSetupData({ email: res.data.email });
            setMethod('email');
            setStep('email');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'E-Mail-Code konnte nicht versendet werden');
        } finally {
            setLoading(false);
        }
    };

    const verify = async (e) => {
        e?.preventDefault?.();
        if (!code.trim()) return;
        setLoading(true);
        try {
            const res = await twoFAApi.forcedSetupVerify(session.setup_id, method, code.trim());
            setBackupCodes(res.data.backup_codes || []);
            setFinalLogin({ token: res.data.token, user: res.data.user });
            setStep('backup');
            toast.success('Zwei-Faktor-Authentifizierung aktiviert');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Code ungültig');
        } finally {
            setLoading(false);
        }
    };

    const copyAll = () => {
        const text = backupCodes.join('\n');
        try {
            navigator.clipboard.writeText(text);
            setCopied(true);
            toast.success('Backup-Codes in Zwischenablage kopiert');
            setTimeout(() => setCopied(false), 2500);
        } catch {
            toast.error('Kopieren fehlgeschlagen — bitte manuell markieren');
        }
    };

    const downloadTxt = () => {
        const content = [
            'BGPR-Akademie — 2FA Backup-Codes',
            '',
            `Konto: ${session.user_email || finalLogin?.user?.email || ''}`,
            `Erstellt: ${new Date().toLocaleString('de-DE')}`,
            '',
            'Bewahren Sie diese Codes sicher auf. Jeder Code ist nur einmal',
            'verwendbar und ersetzt einen 2FA-Code, falls Sie keinen Zugriff',
            'auf Ihre Authenticator-App oder Ihr E-Mail-Postfach haben.',
            '',
            ...backupCodes.map((c, i) => `  ${(i + 1).toString().padStart(2, ' ')}.  ${c}`),
        ].join('\n');
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `BGPR-Backup-Codes-${(session.user_email || 'admin').split('@')[0]}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setDownloaded(true);
        toast.success('Backup-Codes als .txt heruntergeladen');
    };

    const finish = () => {
        if (!downloaded && !copied) {
            if (!window.confirm('Sie haben die Backup-Codes weder kopiert noch heruntergeladen. Wirklich fortfahren? Sie sehen die Codes danach NIE wieder.')) {
                return;
            }
        }
        onCompleted?.(finalLogin);
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-muted p-4">
            <Card className="w-full max-w-lg shadow-lg" data-testid="forced-2fa-setup">
                <CardHeader className="text-center pb-2">
                    <div className="mx-auto mb-4 p-3 bg-primary rounded-xl w-fit">
                        {step === 'backup'
                            ? <ShieldCheck className="h-8 w-8 text-white" />
                            : <Shield className="h-8 w-8 text-white" />}
                    </div>
                    <CardTitle className="text-2xl font-heading font-bold">
                        Zwei-Faktor-Authentifizierung einrichten
                    </CardTitle>
                    <CardDescription>
                        {session.user_name ? `Willkommen, ${session.user_name}. ` : ''}
                        Als Admin-Konto ist 2FA Pflicht. Bitte vor dem ersten Login einrichten.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {step === 'method' && (
                        <div className="space-y-3" data-testid="forced-2fa-method-select">
                            <p className="text-sm text-muted-foreground mb-3">
                                Wählen Sie Ihre bevorzugte Methode:
                            </p>
                            <button
                                type="button"
                                onClick={startTotp}
                                disabled={loading}
                                data-testid="forced-2fa-pick-totp"
                                className="w-full text-left rounded-lg border p-4 hover:border-primary hover:bg-muted/50 transition-colors flex items-start gap-3 disabled:opacity-50"
                            >
                                <div className="p-2 bg-primary/10 rounded-md">
                                    <Smartphone className="h-5 w-5 text-primary" />
                                </div>
                                <div className="flex-1">
                                    <div className="font-medium">Authenticator-App (TOTP)</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                        Empfohlen — funktioniert auch ohne Mailserver. Google Authenticator,
                                        Authy, 1Password u. v. m.
                                    </div>
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={startEmail}
                                disabled={loading}
                                data-testid="forced-2fa-pick-email"
                                className="w-full text-left rounded-lg border p-4 hover:border-primary hover:bg-muted/50 transition-colors flex items-start gap-3 disabled:opacity-50"
                            >
                                <div className="p-2 bg-primary/10 rounded-md">
                                    <Mail className="h-5 w-5 text-primary" />
                                </div>
                                <div className="flex-1">
                                    <div className="font-medium">E-Mail-Code</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                        Bei jeder Anmeldung erhalten Sie einen 6-stelligen Code an
                                        Ihre E-Mail-Adresse.
                                    </div>
                                </div>
                            </button>
                            {loading && (
                                <div className="flex justify-center pt-2">
                                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                </div>
                            )}
                            {onCancel && (
                                <Button type="button" variant="ghost" className="w-full mt-2" onClick={onCancel} data-testid="forced-2fa-cancel">
                                    <ArrowLeft className="mr-2 h-4 w-4" /> Abbrechen
                                </Button>
                            )}
                        </div>
                    )}

                    {step === 'totp' && setupData && (
                        <form onSubmit={verify} className="space-y-4" data-testid="forced-2fa-totp-form">
                            <p className="text-sm">
                                Scannen Sie den QR-Code mit Ihrer Authenticator-App und geben Sie
                                dann den 6-stelligen Code ein, den die App anzeigt.
                            </p>
                            <div className="flex justify-center">
                                <img src={setupData.qr_code} alt="2FA QR-Code" className="w-48 h-48 border rounded-md bg-white p-2" />
                            </div>
                            <details className="rounded-md border bg-muted/40 p-3 text-xs">
                                <summary className="cursor-pointer text-muted-foreground">
                                    QR-Code lässt sich nicht scannen? Geheimschlüssel anzeigen
                                </summary>
                                <p className="font-mono mt-2 break-all select-all">{setupData.secret}</p>
                            </details>
                            <div className="space-y-2">
                                <Label htmlFor="totp-code">Code aus Authenticator-App</Label>
                                <Input
                                    id="totp-code"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    placeholder="123456"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    required
                                    autoFocus
                                    className="text-center font-mono tracking-[0.4em] text-lg"
                                    data-testid="forced-2fa-totp-code"
                                />
                            </div>
                            <Button type="submit" className="w-full" disabled={loading || !code.trim()} data-testid="forced-2fa-totp-submit">
                                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                                Bestätigen & 2FA aktivieren
                            </Button>
                            <Button type="button" variant="ghost" className="w-full" onClick={() => { setStep('method'); setCode(''); }} data-testid="forced-2fa-totp-back">
                                <ArrowLeft className="mr-2 h-4 w-4" /> Andere Methode wählen
                            </Button>
                        </form>
                    )}

                    {step === 'email' && setupData && (
                        <form onSubmit={verify} className="space-y-4" data-testid="forced-2fa-email-form">
                            <p className="text-sm">
                                Wir haben einen 6-stelligen Code an{' '}
                                <strong className="font-mono">{setupData.email}</strong> versendet.
                                Der Code ist 10 Minuten gültig.
                            </p>
                            <div className="space-y-2">
                                <Label htmlFor="email-code">E-Mail-Code</Label>
                                <Input
                                    id="email-code"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    placeholder="123456"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    required
                                    autoFocus
                                    className="text-center font-mono tracking-[0.4em] text-lg"
                                    data-testid="forced-2fa-email-code"
                                />
                            </div>
                            <Button type="submit" className="w-full" disabled={loading || !code.trim()} data-testid="forced-2fa-email-submit">
                                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                                Bestätigen & 2FA aktivieren
                            </Button>
                            <Button type="button" variant="ghost" className="w-full" onClick={() => { setStep('method'); setCode(''); }} data-testid="forced-2fa-email-back">
                                <ArrowLeft className="mr-2 h-4 w-4" /> Andere Methode wählen
                            </Button>
                        </form>
                    )}

                    {step === 'backup' && (
                        <div className="space-y-4" data-testid="forced-2fa-backup-step">
                            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                                <div className="flex items-start gap-2">
                                    <KeyRound className="h-5 w-5 mt-0.5 shrink-0" />
                                    <div>
                                        <strong>Wichtig:</strong> Diese Backup-Codes sehen Sie nur dieses
                                        eine Mal. Bewahren Sie sie sicher auf — z. B. im Passwort-Manager.
                                        Jeder Code ist einmal verwendbar, wenn Sie keinen Zugriff auf Ihre
                                        2FA-Methode haben.
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-muted/50 p-3 rounded-md" data-testid="forced-2fa-backup-codes">
                                {backupCodes.map((c) => (
                                    <div key={c} className="px-2 py-1 bg-background rounded border tracking-wider">
                                        {c}
                                    </div>
                                ))}
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <Button type="button" variant="outline" className="flex-1" onClick={copyAll} data-testid="forced-2fa-copy-codes">
                                    {copied ? <Check className="mr-2 h-4 w-4 text-emerald-600" /> : <Copy className="mr-2 h-4 w-4" />}
                                    {copied ? 'Kopiert' : 'Codes kopieren'}
                                </Button>
                                <Button type="button" variant="outline" className="flex-1" onClick={downloadTxt} data-testid="forced-2fa-download-codes">
                                    {downloaded ? <Check className="mr-2 h-4 w-4 text-emerald-600" /> : <Download className="mr-2 h-4 w-4" />}
                                    {downloaded ? 'Heruntergeladen' : 'Als .txt speichern'}
                                </Button>
                            </div>
                            <Button type="button" className="w-full bg-primary hover:bg-primary/90" onClick={finish} data-testid="forced-2fa-finish">
                                <ShieldCheck className="mr-2 h-4 w-4" /> Codes gesichert — jetzt anmelden
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
